import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DashboardService } from '../dashboard.service';
import { LessonModel, StudentExercisePerformanceModel, StudentSubjectStatsModel } from '@generativ/much-smarter-models';
import { StudentService } from '../../student/student.service';
import { Subscription } from 'rxjs';
import { ProgressCircleComponent } from '../../../shared/progress-circle/progress-circle.component';
import { NgbAccordionDirective, NgbAccordionItem, NgbAccordionHeader, NgbAccordionToggle, NgbAccordionButton, NgbAccordionCollapse, NgbAccordionBody } from '@ng-bootstrap/ng-bootstrap/accordion';
import { NgbProgressbar } from '@ng-bootstrap/ng-bootstrap/progressbar';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap/collapse';


@Component({
    selector: 'app-dashboard-subject',
    templateUrl: './dashboard-subject.component.html',
    styleUrls: ['./dashboard-subject.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ProgressCircleComponent, RouterLink, NgbAccordionDirective, NgbAccordionItem, NgbAccordionHeader, NgbAccordionToggle, NgbAccordionButton, NgbProgressbar, NgbCollapse, NgbAccordionCollapse, NgbAccordionBody]
})

export class DashboardSubjectComponent implements OnInit, OnDestroy {
  subscriptions: Subscription = new Subscription();
  @Input() courseId: number;
  @Input() subject: string;
  @Input() lessons: LessonModel[];
  subjectStats: StudentSubjectStatsModel;

  @Input() video: any;

  completionPercentage = 0;
  lastExerciseAccuracy = 0;
  lifetimeAccuracy = 0;
  completedExerciseIds = [];
  lessonCompletionMap: Map<number, number>;
  exercisePerformanceMetrics: Map<number, StudentExercisePerformanceModel>;

  constructor(
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
    private dashboardService: DashboardService,
    private studentService: StudentService
  ) {
    this.lessonCompletionMap = new Map<number, number>();
  }

  ngOnInit() {
    this.getExercisePerformance();
    this.subscriptions.add(
      this.dashboardService.getStudentSubjectStats(this.courseId, this.subject)
        .subscribe((stats) => {
          this.subjectStats = stats;
          const completedIds = stats?.completedSubjectExercieIds ?? [];
          const lastExercise = stats?.lastExerciseStatsModel;

          if (stats?.totalSubjectProblems) {
            this.completionPercentage = Math.round(
              completedIds.length / (stats.totalSubjectExercises || 1) * 100
            );
            if (lastExercise?.problemsAttempted) {
              this.lastExerciseAccuracy = Math.round(
                lastExercise.problemsCorrect / lastExercise.problemsAttempted * 100
              );
            }
            this.lifetimeAccuracy = Math.round(
              stats.correctSubjectProblems / stats.totalSubjectProblems * 100
            );
            this.completedExerciseIds = completedIds;
          }

          for (const lesson of this.lessons ?? []) {
            let completedExercises = 0;
            for (const exercise of lesson.exercises ?? []) {
              if (completedIds.includes(exercise.id)) {
                ++completedExercises;
              }
            }
            this.lessonCompletionMap.set(
              lesson.id,
              lesson.exercises?.length
                ? completedExercises / lesson.exercises.length * 100
                : 0
            );
          }
        }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getExercisePerformance() {
    this.subscriptions.add(
      this.studentService.getExercisePerformance()
      .subscribe((result: { keys: number[]; values: StudentExercisePerformanceModel[] }) => {
        this.exercisePerformanceMetrics = new Map<number, StudentExercisePerformanceModel>();
        if (result && result.hasOwnProperty('keys')) {
          for (let i = 0; i < result.keys.length; ++i) {
            this.exercisePerformanceMetrics.set(result.keys[i], result.values[i]);
          }
        }
      }));
  }

  open(content) {
    this.modalService.open(content, {size: 'lg'});
  }

  getLessonCompletion(lessonId: number): number {
    return Math.round(this.lessonCompletionMap.get(lessonId)) || 0;
  }
}
